import {
    Component,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    ElementRef,
    SimpleChanges,
    ContentChild,
    TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AlertService, ButtonColors, ButtonModule } from '@ed---interne/ng-uui-components';
import { DropZoneDirective } from './dropzone.directive';

export type FileSizeUnit = 'Ko' | 'Mo';

@Component({
    selector: 'ed-input-file',
    templateUrl: './ed-input-file.component.html',
    styleUrls: ['./ed-input-file.component.scss'],
    standalone: true,
    imports: [CommonModule, DropZoneDirective, ButtonModule],
    providers: [AlertService],
})
export class EdInputFileComponent {
    public buttonColors = ButtonColors;
    public file: File | null = null;
    public isFileOversized: boolean | 0 | null = false;

    @ContentChild(TemplateRef) errorTemplate?: TemplateRef<any>;

    @ViewChild('fileInputDrag')
    fileInput?: ElementRef<HTMLInputElement>;

    @Input() innerId = '';
    @Input() placeholder = '';
    @Input() isUploading: boolean = false;
    @Input() allowedFormat: string[] | null = null;
    @Output() onSubmit = new EventEmitter<File | null>();
    @Input() maxFileSizeInMo: number | null = null;
    @Input() fileTypeLabel: string = '';
    @Input() fileSizeUnit: FileSizeUnit = 'Mo';
    @Input() isAnyErrorDefined: boolean = false;

    @Input() resetTrigger: number = 0;

    constructor(private readonly alertService: AlertService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['resetTrigger']) {
            this.resetInput();
        }
    }

    get accept(): string | null {
        if (!this.allowedFormat?.length) {
            return null;
        }

        // ex: ['xlsx', 'csv'] → '.xlsx,.csv'
        return this.allowedFormat.map((ext) => (ext.startsWith('.') ? ext : `.${ext}`)).join(',');
    }

    async uploadByEvent(event: any) {
        const files: FileList = event.target.files;
        const file = files.item(0);
        event.stopPropagation();
        event.preventDefault();
        this.upload(file);
    }

    removeFile() {
        this.resetInput();
        this.onSubmit.emit(null);
    }

    async upload(file: File | null) {
        if (!file) {
            return;
        }

        this.isFileOversized =
            this.maxFileSizeInMo != null && file.size > this.maxFileSizeInMo * 1024 * 1024;

        if (
            this.allowedFormat &&
            !this.allowedFormat
                .map((format) => format.toLocaleLowerCase())
                .includes(file?.name.split('.').pop()?.toLocaleLowerCase() || '&ù$')
        ) {
            this.alertService.error('Document', 'Format de fichier non valide');
            this.resetInput();
            return;
        }

        this.file = file;
        this.onSubmit.emit(this.file);
    }

    get fileSizeMo(): number | null {
        return this.file ? +(this.file.size / 1024 / 1024).toFixed(1) : null;
    }

    get fileSize(): number | null {
        if (!this.file) {
            return null;
        }

        const sizeInBytes = this.file.size;

        switch (this.fileSizeUnit) {
            case 'Ko':
                return +(sizeInBytes / 1024).toFixed(1);
            case 'Mo':
            default:
                return +(sizeInBytes / 1024 / 1024).toFixed(1);
        }
    }

    get fileSizeUnitLabel(): string {
        return this.fileSizeUnit;
    }

    private resetInput() {
        this.file = null;
        this.isFileOversized = false;
        this.isAnyErrorDefined = false;
        if (this.fileInput) {
            this.fileInput.nativeElement.value = '';
        }
    }

    reloadFile() {
        if (this.fileInput) {
            this.fileInput.nativeElement.value = '';
            this.fileInput.nativeElement.click();
        }
    }
}
